import { isSystemPersonType, isSystemTimeType, readSystemPropertyValue, type RowMeta } from "@/lib/systemProperties";
import { parseDateCell, serializeDateCell } from "@/lib/date-value";
export type FormulaReturnType = "text" | "number" | "boolean" | "date" | "id";

export interface FormulaPropertyDefinition {
  id: string;
  name: string;
  type: string;
  formula?: string;
  formulaReturnType?: FormulaReturnType;
  options?: Array<{ name: string; color?: string }>;
  /**
   * For relation properties, the id of the datasource this relation targets.
   * Carried through so a related row's OWN relation formulas can be resolved
   * against their datasource's schema, not just the root board schema.
   */
  linkedDatabaseId?: string;
}

export interface FormulaNoteLike {
  _id?: unknown;
  id?: string;
  title?: string;
  databaseProperties?: Record<string, any>;
  formulaErrors?: Record<string, string>;
  [key: string]: any;
}

export interface FormulaEvaluationResult {
  value: any;
  rawValue: any;
  returnType: FormulaReturnType | "raw";
  error?: string;
}

export interface FormulaRuntimeResult {
  note: FormulaNoteLike;
  results: Record<string, FormulaEvaluationResult>;
  errors: Record<string, string>;
}

/**
 * A related row handed back by a {@link RelationResolver}: the row itself plus
 * the property schema of ITS datasource, so sub-expressions evaluated against
 * it resolve `prop("…")` in the related table's namespace (including that
 * table's own formula properties, recursively, depth-capped).
 */
export interface RelatedRowContext {
  note: FormulaNoteLike;
  properties: Record<string, FormulaPropertyDefinition>;
}

/**
 * Resolves a relation cell into related rows. Injected by the call site
 * (which owns the note/datasource caches); the evaluator itself stays free of
 * data-fetching. Return null when the relation target is not loaded — the
 * aggregate functions then surface a clear error instead of a wrong 0.
 *
 * `linkedDatabaseId` is the target datasource of the relation property being
 * resolved, taken from the schema of the row CURRENTLY being evaluated. It is
 * passed so a related row's own relation formulas resolve against their own
 * datasource, not just the root board schema (nested / recursive aggregation).
 */
export type RelationResolver = (
  relationPropertyId: string,
  relatedIds: string[],
  linkedDatabaseId?: string
) => RelatedRowContext[] | null;

export interface FormulaRuntimeOptions {
  resolveRelation?: RelationResolver;
}

interface CompiledFormula {
  source: string;
  sanitized: string;
  fn: (ctx: FormulaExecutionContext) => any;
}

interface FormulaExecutionContext {
  prop: (propertyName: string) => any;
  today: () => Date;
  relatedCount: (relationName: string) => number;
  countIf: (relationName: string, expression: string) => number;
  sumBy: (relationName: string, expression: string) => number;
  someBy: (relationName: string, expression: string) => boolean;
  everyBy: (relationName: string, expression: string) => boolean;
  joinBy: (relationName: string, expression: string, separator?: string) => string;
  if: <T>(condition: any, truthy: T, falsy: T) => T;
  ifs: (...args: any[]) => any;
  and: (...values: any[]) => boolean;
  or: (...values: any[]) => boolean;
  not: (value: any) => boolean;
  concat: (...values: any[]) => string;
  format: (value: any, locale?: string) => string;
  length: (value: any) => number;
  abs: (value: any) => number;
  round: (value: any, precision?: number) => number;
  floor: (value: any) => number;
  ceil: (value: any) => number;
  max: (...values: any[]) => number | null;
  min: (...values: any[]) => number | null;
  sum: (...values: any[]) => number | null;
  average: (...values: any[]) => number | null;
  contains: (target: any, search: any) => boolean;
  empty: (value: any) => boolean;
  toNumber: (value: any) => number | null;
  toString: (value: any) => string;
  coalesce: (...values: any[]) => any;
  now: () => Date;
  dateBetween: (date1: any, date2: any, unit?: DateDiffUnit) => number | null;
  dateAdd: (value: any, amount: number, unit?: DateDiffUnit) => Date | null;
  dateSubtract: (value: any, amount: number, unit?: DateDiffUnit) => Date | null;
  timestamp: (value?: any) => number | null;
  pi: () => number;
  e: () => number;
  cbrt: (value: any) => number;
  exp: (value: any) => number;
  ln: (value: any) => number;
  log10: (value: any) => number;
  log: (value: any, base?: any) => number;
  pow: (value: any, exponent: any) => number;
  sqrt: (value: any) => number;
  mod: (value: any, divisor: any) => number;
  roundup: (value: any, precision?: number) => number;
  rounddown: (value: any, precision?: number) => number;
  sign: (value: any) => number;
  sin: (value: any) => number;
  cos: (value: any) => number;
  tan: (value: any) => number;
  asin: (value: any) => number;
  acos: (value: any) => number;
  atan: (value: any) => number;
  equal: (left: any, right: any) => boolean;
  unequal: (left: any, right: any) => boolean;
  larger: (left: any, right: any) => boolean;
  largerEq: (left: any, right: any) => boolean;
  smaller: (left: any, right: any) => boolean;
  smallerEq: (left: any, right: any) => boolean;
  lower: (value: any) => string;
  upper: (value: any) => string;
  trim: (value: any) => string;
  replace: (text: any, pattern: any, replacement: any) => string;
  replaceAll: (text: any, pattern: any, replacement: any) => string;
  slice: (text: any, start: any, length?: any) => string;
  startsWith: (text: any, search: any) => boolean;
  endsWith: (text: any, search: any) => boolean;
  join: (value: any, separator?: any) => string;
  year: (date: any) => number | null;
  month: (date: any) => number | null;
  date: (date: any) => number | null;
  day: (date: any) => number | null;
  hour: (date: any) => number | null;
  minute: (date: any) => number | null;
  second: (date: any) => number | null;
  formatDate: (date: any, format: string) => string;
  style: (text: any, ...styles: string[]) => string;
}

type DateDiffUnit = "milliseconds" | "seconds" | "minutes" | "hours" | "days" | "weeks" | "months" | "quarters" | "years";

interface FormulaRuntimeContext {
  properties: Record<string, FormulaPropertyDefinition>;
  nameToId: Map<string, string>;
  compiledCache: Map<string, CompiledFormula>;
  formulaPropertyIds: string[];
  /** Optional relation resolver injected via createFormulaRuntime options. */
  resolveRelation?: RelationResolver;
  /**
   * Cross-datasource recursion depth. A formula on table A may aggregate rows
   * of table B whose own formulas aggregate table A — without a cap that
   * mutual recursion would never terminate (the per-note `stack` only guards
   * cycles WITHIN one datasource).
   */
  depth: number;
}

/** Hard ceiling for relation-hopping recursion (A → B → A → …). */
const MAX_RELATION_DEPTH = 4;

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAY_NAMES = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
];

// Day-only-ness is RECORDED, never inferred. It used to be inferred from the
// instant sitting at UTC midnight, which was wrong twice over: a real 05:30
// cell in a +05:30 zone lands there (so timed values read as day-only), and
// dateAdd moves the instant (so a day-only value stops reading as one after
// arithmetic — 23h across a DST boundary, not 24h).
//
// A WeakSet rather than a field because Date has nowhere to put one; entries
// die with the Date. Every producer of a day-only Date must call markDayOnly.
const DAY_ONLY = new WeakSet<Date>();

function markDayOnly(d: Date): Date {
  DAY_ONLY.add(d);
  return d;
}

function isDateOnlyValue(d: Date): boolean {
  return DAY_ONLY.has(d);
}

/** Local midnight of the same calendar day, marked day-only. */
function toDayOnly(d: Date): Date {
  return markDayOnly(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
}

/** Units that move whole days, so a day-only operand stays day-only. */
const DAY_SCALE_UNITS = new Set(["days", "weeks", "months", "quarters", "years"]);

/**
 * Child runtime contexts for related datasources, cached per property-schema
 * object so repeated aggregation over the same table reuses compiled formulas.
 */
const relatedRuntimeCache = new WeakMap<
  Record<string, FormulaPropertyDefinition>,
  FormulaRuntimeContext
>();

/**
 * Normalise a raw relation cell into an id array. Mirrors relationUtils'
 * RelationValue union: bare string (relationLimit "single"), string[], and
 * the legacy object forms {noteId} / {id} / {_id}.
 */
function relationCellIds(raw: unknown): string[] {
  const list = Array.isArray(raw) ? raw : raw == null ? [] : [raw];
  return list
    .map((entry: any) =>
      typeof entry === "string"
        ? entry
        : String(entry?.noteId ?? entry?.id ?? entry?._id ?? entry?.pageId ?? "")
    )
    .filter(Boolean);
}

const DEFAULT_RETURN_TYPE: FormulaReturnType = "text";
const TITLE_ALIASES = ["title", "name"];

const ALLOWED_IDENTIFIER_FUNCTIONS = new Set([
  "prop",
  "if",
  "ifs",
  "and",
  "or",
  "not",
  "concat",
  "format",
  "length",
  "abs",
  "round",
  "floor",
  "ceil",
  "max",
  "min",
  "sum",
  "average",
  "contains",
  "empty",
  "toNumber",
  "toString",
  "coalesce",
  "now",
  "dateBetween",
  "dateAdd",
  "dateSubtract",
  "timestamp",
  "pi",
  "e",
  "cbrt",
  "exp",
  "ln",
  "log10",
  "log",
  "pow",
  "sqrt",
  "mod",
  "roundup",
  "rounddown",
  "sign",
  "sin",
  "cos",
  "tan",
  "asin",
  "acos",
  "atan",
  "equal",
  "unequal",
  "larger",
  "largerEq",
  "smaller",
  "smallerEq",
  "lower",
  "upper",
  "trim",
  "replace",
  "replaceAll",
  "slice",
  "startsWith",
  "endsWith",
  "join",
  "year",
  "month",
  "date",
  "day",
  "hour",
  "minute",
  "second",
  "formatDate",
  "style",
  // Date convenience
  "today",
  // Relation traversal + aggregation. The second argument of the *If/*By
  // functions is a STRING sub-expression, compiled with this same engine and
  // evaluated once per related row with `prop()` bound to THAT row and its
  // own datasource schema.
  "relatedCount",
  "countIf",
  "sumBy",
  "someBy",
  "everyBy",
  "joinBy",
]);

const ALLOWED_LITERALS = new Set(["true", "false", "null"]);

// Functions whose arguments must be evaluated lazily so unused branches never run
// (mirrors spreadsheet/Notion semantics). Their arguments are compiled into thunks
// and only invoked on demand by the runtime implementations below.
const SHORT_CIRCUIT_FUNCTIONS = new Set(["if", "ifs", "and", "or", "coalesce"]);

const ALLOWED_SINGLE_OPERATORS = new Set(["+", "-", "*", "/", "%", "^", ">", "<", "!", "(", ")", ","]);
const ALLOWED_MULTI_OPERATORS = new Set(["**", "==", "!=", ">=", "<=", "&&", "||"]);

// Comparison operators are compiled into VALUE-aware helper calls rather than
// raw JS operators. Raw `a == b` on two Date objects is reference equality (so
// today() never equals a date cell), and raw `a > b` between a Date and a
// "YYYY-MM-DD" string coerces to NaN — both wrong. Routing them through
// ctx.equal/larger/… (backed by compareValues) fixes dates, numbers, and text
// uniformly, matching Notion. See compileTokensToJs().
const COMPARISON_TO_HELPER: Record<string, string> = {
  "==": "equal",
  "!=": "unequal",
  ">": "larger",
  ">=": "largerEq",
  "<": "smaller",
  "<=": "smallerEq",
};

const EPSILON = 1e-12;

function toNumberOrThrow(value: any, functionName: string, argumentPosition: number): number {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    throw new Error(`${functionName}() argument ${argumentPosition} must be a finite number`);
  }
  return num;
}


function toOptionalNumber(value: any): number | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function toStringValue(value: any): string {
  if (value === null || value === undefined) {
    return "";
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function compareValues(left: any, right: any): number {
  // Dates first: date cells are stored as "YYYY-MM-DD" strings while
  // today()/now()/dateAdd() return Date objects. When either side is a real
  // Date, coerce BOTH to dates and compare the instant — otherwise a Date never
  // equals its own string form (`today() == prop("Due")` would be false).
  if (left instanceof Date || right instanceof Date) {
    const leftDate = toDate(left);
    const rightDate = toDate(right);
    if (leftDate && rightDate) {
      const diff = leftDate.getTime() - rightDate.getTime();
      return diff < 0 ? -1 : diff > 0 ? 1 : 0;
    }
  }

  const leftNumber = toOptionalNumber(left);
  const rightNumber = toOptionalNumber(right);
  if (leftNumber !== null && rightNumber !== null) {
    if (Math.abs(leftNumber - rightNumber) < EPSILON) {
      return 0;
    }
    return leftNumber - rightNumber;
  }

  // Case-SENSITIVE text comparison, matching Notion (`"A" == "a"` is false) and
  // the previous raw `==` operator behaviour.
  const leftText = toStringValue(left);
  const rightText = toStringValue(right);
  return leftText.localeCompare(rightText);
}

function parsePattern(text: any): RegExp | string {
  if (text instanceof RegExp) {
    return text;
  }

  const candidate = String(text ?? "");
  if (candidate.startsWith("/") && candidate.lastIndexOf("/") > 0) {
    try {
      const closing = candidate.lastIndexOf("/");
      const body = candidate.slice(1, closing);
      const flags = candidate.slice(closing + 1);
      return new RegExp(body, flags);
    } catch {
      // ignore invalid regexp, fall back to literal replacement
    }
  }

  return candidate;
}

enum TokenType {
  Number = "number",
  String = "string",
  Identifier = "identifier",
  Operator = "operator",
  Paren = "paren",
  Comma = "comma",
}

interface Token {
  type: TokenType;
  value: string;
  position: number;
}

export interface FormulaRuntime {
  hasFormulas: boolean;
  evaluatePropertyForNote: (
    propertyId: string,
    note: FormulaNoteLike,
    memo?: Map<string, FormulaEvaluationResult>,
    stack?: Set<string>
  ) => FormulaEvaluationResult;
  recomputeFormulasForNote: (note: FormulaNoteLike) => FormulaRuntimeResult;
  recomputeFormulasForNotes: (notes: FormulaNoteLike[]) => FormulaRuntimeResult[];
}

export function createFormulaRuntime(
  properties: Record<string, FormulaPropertyDefinition>,
  options: FormulaRuntimeOptions = {}
): FormulaRuntime {
  const normalizedProperties: Record<string, FormulaPropertyDefinition> = {};
  const nameToId = new Map<string, string>();

  Object.entries(properties || {}).forEach(([id, prop]) => {
    const normalized: FormulaPropertyDefinition = {
      ...prop,
      id: prop.id ?? id,
      formula: prop.formula ?? "",
      formulaReturnType: prop.formulaReturnType ?? DEFAULT_RETURN_TYPE,
    };
    normalizedProperties[id] = normalized;
    nameToId.set(prop.name, id);
    nameToId.set(prop.name.toLowerCase(), id);
  });

  const formulaPropertyIds = Object.entries(normalizedProperties)
    .filter(([, prop]) => prop.type === "formula")
    .map(([id]) => id);

  const context: FormulaRuntimeContext = {
    properties: normalizedProperties,
    nameToId,
    compiledCache: new Map<string, CompiledFormula>(),
    formulaPropertyIds,
    resolveRelation: options.resolveRelation,
    depth: 0,
  };

  const evaluatePropertyForNote = (
    propertyId: string,
    note: FormulaNoteLike,
    memo: Map<string, FormulaEvaluationResult> = new Map(),
    stack: Set<string> = new Set()
  ) => evaluateProperty(propertyId, note, context, memo, stack);

  const recomputeFormulasForNote = (note: FormulaNoteLike): FormulaRuntimeResult => {
    if (context.formulaPropertyIds.length === 0) {
      return {
        note: { ...note },
        results: {},
        errors: {},
      };
    }

    const workingNote: FormulaNoteLike = {
      ...note,
      databaseProperties: { ...(note.databaseProperties ?? {}) },
    };
    const memo = new Map<string, FormulaEvaluationResult>();
    const errors: Record<string, string> = {};
    const results: Record<string, FormulaEvaluationResult> = {};

    for (const formulaId of context.formulaPropertyIds) {
      const result = evaluateProperty(formulaId, workingNote, context, memo, new Set());

      results[formulaId] = result;
      if (!workingNote.databaseProperties) {
        workingNote.databaseProperties = {};
      }
      workingNote.databaseProperties[formulaId] = result.error ? null : result.value ?? null;
      if (result.error) {
        errors[formulaId] = result.error;
      }
    }

    workingNote.formulaErrors = Object.keys(errors).length > 0 ? errors : undefined;

    return {
      note: workingNote,
      results,
      errors,
    };
  };

  const recomputeFormulasForNotes = (notes: FormulaNoteLike[]): FormulaRuntimeResult[] => {
    if (!Array.isArray(notes) || notes.length === 0) {
      return [];
    }
    return notes.map((note) => recomputeFormulasForNote(note));
  };

  return {
    hasFormulas: context.formulaPropertyIds.length > 0,
    evaluatePropertyForNote,
    recomputeFormulasForNote,
    recomputeFormulasForNotes,
  };
}

function evaluateProperty(
  propertyId: string,
  note: FormulaNoteLike,
  context: FormulaRuntimeContext,
  memo: Map<string, FormulaEvaluationResult>,
  stack: Set<string>
): FormulaEvaluationResult {
  if (memo.has(propertyId)) {
    return memo.get(propertyId)!;
  }

  const property = context.properties[propertyId];
  if (!property) {
    const missingResult: FormulaEvaluationResult = {
      value: null,
      rawValue: null,
      returnType: "raw",
      error: `Property '${propertyId}' not found`,
    };
    memo.set(propertyId, missingResult);
    return missingResult;
  }

  if (property.type !== "formula") {
    const rawValue = readPropertyValue(propertyId, property, note, context);
    const result: FormulaEvaluationResult = {
      value: rawValue,
      rawValue,
      returnType: "raw",
    };
    memo.set(propertyId, result);
    return result;
  }

  const expression = property.formula?.trim();
  if (!expression) {
    const emptyResult: FormulaEvaluationResult = {
      value: null,
      rawValue: null,
      returnType: property.formulaReturnType ?? DEFAULT_RETURN_TYPE,
    };
    memo.set(propertyId, emptyResult);
    return emptyResult;
  }

  if (stack.has(propertyId)) {
    const cycleResult: FormulaEvaluationResult = {
      value: null,
      rawValue: null,
      returnType: property.formulaReturnType ?? DEFAULT_RETURN_TYPE,
      error: "Circular formula reference detected",
    };
    memo.set(propertyId, cycleResult);
    return cycleResult;
  }

  stack.add(propertyId);
  let rawValue: any = null;
  let executionError: string | undefined;

  try {
    const compiled = compileFormula(expression, context);
    const execCtx = buildExecutionContext(propertyId, note, context, memo, stack);
    rawValue = compiled.fn(execCtx);
  } catch (error) {
    executionError =
      error instanceof Error ? error.message : "Failed to execute formula";
  } finally {
    stack.delete(propertyId);
  }

  const coerced = coerceValueForReturnType(
    rawValue,
    property.formulaReturnType ?? DEFAULT_RETURN_TYPE
  );

  const error = executionError ?? coerced.error;
  const finalResult: FormulaEvaluationResult = {
    value: error ? null : coerced.value,
    rawValue,
    returnType: property.formulaReturnType ?? DEFAULT_RETURN_TYPE,
    error,
  };

  memo.set(propertyId, finalResult);
  return finalResult;
}

function compileFormula(
  expression: string,
  context: FormulaRuntimeContext
): CompiledFormula {
  const cached = context.compiledCache.get(expression);
  if (cached) {
    return cached;
  }

  const sanitized = sanitizeExpression(expression);
  let fn: (ctx: FormulaExecutionContext) => any;

  try {
    // eslint-disable-next-line no-new-func
    fn = new Function("ctx", `"use strict"; return (${sanitized});`) as (
      ctx: FormulaExecutionContext
    ) => any;
  } catch (error) {
    const message =
      error instanceof Error ? error.message : "Failed to compile formula";
    throw new Error(`Formula compilation error: ${message}`);
  }

  const compiled: CompiledFormula = { source: expression, sanitized, fn };
  context.compiledCache.set(expression, compiled);
  return compiled;
}

/**
 * Relation functions whose FIRST argument is a relation property NAME (a bare
 * string), not a value.
 */
const RELATION_NAME_FUNCTIONS = new Set([
  "relatedCount",
  "countIf",
  "sumBy",
  "someBy",
  "everyBy",
  "joinBy",
]);

/**
 * The property picker always inserts `prop("Name")` (how you read a value), but
 * the relation functions take the relation property NAME as a plain string. So
 * a formula built by INSERTING a property — relatedCount(prop("Tasks")) — would
 * fail with "relation functions require a relation property name". Unwrap
 * `prop("literal")` back to the bare string literal when it is the first
 * argument of a relation function, so inserted and typed formulas both work.
 */
function unwrapInsertedRelationNames(tokens: Token[]): Token[] {
  const out: Token[] = [];
  for (let i = 0; i < tokens.length; i++) {
    const fn = tokens[i]!;
    out.push(fn);
    const open = tokens[i + 1];
    const propId = tokens[i + 2];
    const innerOpen = tokens[i + 3];
    const nameStr = tokens[i + 4];
    const innerClose = tokens[i + 5];
    if (
      fn.type === TokenType.Identifier &&
      RELATION_NAME_FUNCTIONS.has(fn.value) &&
      open?.type === TokenType.Paren && open.value === "(" &&
      propId?.type === TokenType.Identifier && propId.value === "prop" &&
      innerOpen?.type === TokenType.Paren && innerOpen.value === "(" &&
      nameStr?.type === TokenType.String &&
      innerClose?.type === TokenType.Paren && innerClose.value === ")"
    ) {
      out.push(open); // keep the relation function's own "("
      out.push(nameStr); // the relation name, now a bare string literal
      i += 5; // drop  prop ( "…" )
    }
  }
  return out;
}

/**
 * Repair nested quotes produced by inserting a `prop("…")` inside quotes the
 * user had already typed. Two cases, told apart by the character before the
 * outer quote:
 *
 *  - After `(` → a mis-quoted REFERENCE (a relation function's first arg, or a
 *    standalone one). The outer quotes are the mistake — strip them:
 *      countIf("prop("id")", …)  →  countIf(prop("id"), …)
 *  - After `,` → a relation SUB-EXPRESSION (2nd argument). The outer quotes are
 *    the intended string delimiter — keep them and single-quote the inner prop
 *    so the sub-expression stays a valid string:
 *      sumBy("rel", "prop("id")")  →  sumBy("rel", "prop('id')")
 *
 * Real strings and single-quoted sub-expressions are untouched.
 */
function repairInsertedPropQuotes(formula: string): string {
  if (typeof formula !== "string" || !formula.includes('"prop(')) return formula;
  return formula
    .replace(/(,\s*)"prop\("([^"\\]*)"\)"/g, "$1\"prop('$2')\"")
    .replace(/(\(\s*)"prop\("([^"\\]*)"\)"/g, '$1prop("$2")');
}

function sanitizeExpression(expression: string): string {
  const tokens = unwrapInsertedRelationNames(
    tokenize(repairInsertedPropQuotes(expression)),
  );
  return compileTokensToJs(tokens);
}

/**
 * Precedence-climbing parser: turns the (already repaired + tokenized) formula
 * into a JS expression string that calls the ctx.* runtime.
 *
 * Precedence, lowest → highest (Notion-compatible):
 *   or (`or`/`||`)  <  and (`and`/`&&`)  <  comparison (`== != > >= < <=`)  <
 *   additive (`+ -`)  <  multiplicative (`* / %`)  <  power (`**`/`^`, right-
 *   assoc)  <  unary (`- + !`)  <  primary.
 *
 * Comparison operators compile to VALUE-aware helper calls (ctx.equal, …) so
 * dates/numbers/text compare like Notion; every other operator stays raw JS.
 * `and`/`or` also work as infix keywords (short-circuiting via `&&`/`||`).
 * Arguments of short-circuit functions (if/ifs/and/or/coalesce) are wrapped in
 * `() => (…)` thunks so unused branches never run. A relation aggregate's
 * string sub-expression stays an opaque string literal here — it is compiled
 * separately, once per related row.
 */
function compileTokensToJs(tokens: Token[]): string {
  let pos = 0;
  const peek = (offset = 0): Token | undefined => tokens[pos + offset];
  const advance = (): Token => tokens[pos++]!;

  const isOperator = (t: Token | undefined, values: string[]): boolean =>
    !!t && t.type === TokenType.Operator && values.includes(t.value);
  const isParen = (t: Token | undefined, value: string): boolean =>
    !!t && t.type === TokenType.Paren && t.value === value;
  // `and`/`or` are infix operators except when they head a call (`and(` is the
  // function form, handled by parsePrimary).
  const isInfixKeyword = (
    t: Token | undefined,
    keyword: "and" | "or"
  ): boolean =>
    !!t &&
    t.type === TokenType.Identifier &&
    t.value.toLowerCase() === keyword &&
    !isParen(peek(1), "(");
  const matchComma = (): boolean => {
    if (peek()?.type === TokenType.Comma) {
      advance();
      return true;
    }
    return false;
  };

  const parseExpression = (): string => parseOr();

  const parseOr = (): string => {
    let left = parseAnd();
    while (isOperator(peek(), ["||"]) || isInfixKeyword(peek(), "or")) {
      advance();
      left = `(${left} || ${parseAnd()})`;
    }
    return left;
  };

  const parseAnd = (): string => {
    let left = parseComparison();
    while (isOperator(peek(), ["&&"]) || isInfixKeyword(peek(), "and")) {
      advance();
      left = `(${left} && ${parseComparison()})`;
    }
    return left;
  };

  const parseComparison = (): string => {
    let left = parseAdditive();
    while (isOperator(peek(), ["==", "!=", ">", ">=", "<", "<="])) {
      const op = advance().value;
      left = `ctx.${COMPARISON_TO_HELPER[op]}(${left}, ${parseAdditive()})`;
    }
    return left;
  };

  const parseAdditive = (): string => {
    let left = parseMultiplicative();
    while (isOperator(peek(), ["+", "-"])) {
      const op = advance().value;
      left = `(${left} ${op} ${parseMultiplicative()})`;
    }
    return left;
  };

  const parseMultiplicative = (): string => {
    let left = parsePower();
    while (isOperator(peek(), ["*", "/", "%"])) {
      const op = advance().value;
      left = `(${left} ${op} ${parsePower()})`;
    }
    return left;
  };

  const parsePower = (): string => {
    const left = parseUnary();
    if (isOperator(peek(), ["**", "^"])) {
      advance();
      // Right-associative; the paren wrapping also sidesteps JS's `-a ** b`
      // syntax error.
      return `(${left} ** ${parsePower()})`;
    }
    return left;
  };

  const parseUnary = (): string => {
    if (isOperator(peek(), ["-", "+", "!"])) {
      const op = advance().value;
      return `(${op}${parseUnary()})`;
    }
    return parsePrimary();
  };

  const parseCall = (fnName: string): string => {
    advance(); // "("
    const shortCircuit = SHORT_CIRCUIT_FUNCTIONS.has(fnName);
    const args: string[] = [];
    if (!isParen(peek(), ")")) {
      do {
        const arg = parseExpression();
        args.push(shortCircuit ? `() => (${arg})` : arg);
      } while (matchComma());
    }
    if (!isParen(peek(), ")")) {
      throw new Error(`Expected ')' after arguments to ${fnName}()`);
    }
    advance(); // ")"
    return `ctx.${fnName}(${args.join(", ")})`;
  };

  const parsePrimary = (): string => {
    const token = peek();
    if (!token) {
      throw new Error("Unexpected end of formula");
    }
    switch (token.type) {
      case TokenType.Number:
        advance();
        return token.value;
      case TokenType.String:
        advance();
        return JSON.stringify(parseStringLiteral(token.value, token.position));
      case TokenType.Paren: {
        if (token.value !== "(") {
          throw new Error(
            `Unexpected ')' in formula at position ${token.position}`
          );
        }
        advance(); // "("
        const inner = parseExpression();
        if (!isParen(peek(), ")")) {
          throw new Error(
            `Expected ')' in formula at position ${peek()?.position ?? token.position}`
          );
        }
        advance(); // ")"
        return `(${inner})`;
      }
      case TokenType.Identifier: {
        const lower = token.value.toLowerCase();
        if (isParen(peek(1), "(")) {
          if (!ALLOWED_IDENTIFIER_FUNCTIONS.has(token.value)) {
            throw new Error(
              `Unsupported identifier '${token.value}' in formula at position ${token.position}`
            );
          }
          advance(); // function name
          return parseCall(token.value);
        }
        if (ALLOWED_LITERALS.has(lower)) {
          advance();
          return lower;
        }
        // A bare allowed-function identifier (e.g. `pi` written without `()`)
        // keeps its historical `ctx.name` emission rather than throwing.
        if (ALLOWED_IDENTIFIER_FUNCTIONS.has(token.value)) {
          advance();
          return `ctx.${token.value}`;
        }
        throw new Error(
          `Unsupported identifier '${token.value}' in formula at position ${token.position}`
        );
      }
      default:
        throw new Error(
          `Unsupported token '${token.value}' in formula at position ${token.position}`
        );
    }
  };

  const compiled = parseExpression();
  if (pos < tokens.length) {
    const extra = tokens[pos]!;
    throw new Error(
      `Unexpected token '${extra.value}' in formula at position ${extra.position}`
    );
  }
  return compiled;
}

function tokenize(expression: string): Token[] {
  const tokens: Token[] = [];
  const length = expression.length;
  let index = 0;

  while (index < length) {
    const char = expression[index] ?? "";

    if (isWhitespace(char)) {
      index += 1;
      continue;
    }

    if (char === '"' || char === "'") {
      const { token, nextIndex } = readStringToken(expression, index);
      tokens.push(token);
      index = nextIndex;
      continue;
    }

    if (isDigit(char) || (char === "." && isDigit(expression[index + 1] ?? ""))) {
      const { token, nextIndex } = readNumberToken(expression, index);
      tokens.push(token);
      index = nextIndex;
      continue;
    }

    if (isIdentifierStart(char)) {
      const { token, nextIndex } = readIdentifierToken(expression, index);
      tokens.push(token);
      index = nextIndex;
      continue;
    }

    if (char === ",") {
      tokens.push({ type: TokenType.Comma, value: char, position: index });
      index += 1;
      continue;
    }

    if (char === "(" || char === ")") {
      tokens.push({ type: TokenType.Paren, value: char, position: index });
      index += 1;
      continue;
    }

    const twoChar = expression.slice(index, index + 2);
    if (ALLOWED_MULTI_OPERATORS.has(twoChar)) {
      tokens.push({ type: TokenType.Operator, value: twoChar, position: index });
      index += 2;
      continue;
    }

    if (ALLOWED_SINGLE_OPERATORS.has(char)) {
      tokens.push({ type: TokenType.Operator, value: char, position: index });
      index += 1;
      continue;
    }

    throw new Error(
      `Unsupported character '${char}' in formula at position ${index}`
    );
  }

  return tokens;
}

function readStringToken(expression: string, start: number): {
  token: Token;
  nextIndex: number;
} {
  const quote = expression[start];
  let index = start + 1;
  let escaped = false;

  while (index < expression.length) {
    const char = expression[index] ?? "";
    if (escaped) {
      escaped = false;
      index += 1;
      continue;
    }
    if (char === "\\") {
      escaped = true;
      index += 1;
      continue;
    }
    if (char === quote) {
      const value = expression.slice(start, index + 1);
      return {
        token: { type: TokenType.String, value, position: start },
        nextIndex: index + 1,
      };
    }
    index += 1;
  }

  throw new Error(`Unterminated string literal starting at position ${start}`);
}

function readNumberToken(expression: string, start: number): {
  token: Token;
  nextIndex: number;
} {
  let index = start;
  let hasDot = false;
  let hasExponent = false;

  while (index < expression.length) {
    const char = expression[index] ?? "";

    if (isDigit(char)) {
      index += 1;
      continue;
    }

    if (char === "." && !hasDot && !hasExponent) {
      hasDot = true;
      index += 1;
      continue;
    }

    if ((char === "e" || char === "E") && !hasExponent) {
      hasExponent = true;
      index += 1;
      if (expression[index] === "+" || expression[index] === "-") {
        index += 1;
      }
      continue;
    }

    break;
  }

  const value = expression.slice(start, index);
  if (!value || value === "." || value === "+" || value === "-") {
    throw new Error(`Invalid number literal at position ${start}`);
  }

  return {
    token: { type: TokenType.Number, value, position: start },
    nextIndex: index,
  };
}

function readIdentifierToken(expression: string, start: number): {
  token: Token;
  nextIndex: number;
} {
  let index = start;

  while (index < expression.length) {
    const char = expression[index] ?? "";
    if (isIdentifierPart(char)) {
      index += 1;
    } else {
      break;
    }
  }

  const value = expression.slice(start, index);
  return {
    token: { type: TokenType.Identifier, value, position: start },
    nextIndex: index,
  };
}

function parseStringLiteral(raw: string, position: number): string {
  const quote = raw[0];
  if (quote !== '"' && quote !== "'") {
    throw new Error(`Invalid string literal at position ${position}`);
  }

  let jsonReady = raw;
  if (quote === "'") {
    const inner = raw.slice(1, -1).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
    jsonReady = `"${inner}"`;
  }

  try {
    return JSON.parse(jsonReady);
  } catch {
    throw new Error(`Invalid string literal at position ${position}`);
  }
}

function buildExecutionContext(
  propertyId: string,
  note: FormulaNoteLike,
  context: FormulaRuntimeContext,
  memo: Map<string, FormulaEvaluationResult>,
  stack: Set<string>
): FormulaExecutionContext {
  const propFn = (propertyName: string): any => {
    if (typeof propertyName !== "string" || !propertyName.trim()) {
      throw new Error("prop() requires a property name");
    }

    // Id takes priority: references are stored as the property id so they
    // survive renames. Fall back to the display name (and its lowercased form)
    // for formulas authored by typing, and for older name-based formulas.
    const propertyId =
      (context.properties[propertyName] ? propertyName : undefined) ??
      context.nameToId.get(propertyName) ??
      context.nameToId.get(propertyName.toLowerCase());

    if (propertyId) {
      const result = evaluateProperty(propertyId, note, context, memo, stack);
      if (result.error) {
        const dependencyName = context.properties[propertyId]?.name ?? propertyName;
        throw new Error(
          `Dependency '${dependencyName}' error: ${result.error}`
        );
      }
      return result.value ?? null;
    }

    const lowerName = propertyName.toLowerCase();
    if (TITLE_ALIASES.includes(lowerName)) {
      return note.title ?? "";
    }

    throw new Error(`Property '${propertyName}' does not exist`);
  };

  /** Locate a relation property by display name (or id) and read its cell. */
  const relationCell = (
    relationName: string
  ): { propertyId: string; ids: string[]; linkedDatabaseId?: string } => {
    if (typeof relationName !== "string" || !relationName.trim()) {
      throw new Error("relation functions require a relation property name");
    }
    const propertyId =
      context.nameToId.get(relationName) ??
      context.nameToId.get(relationName.toLowerCase()) ??
      (context.properties[relationName] ? relationName : undefined);
    if (!propertyId) {
      throw new Error(`Relation property '${relationName}' does not exist`);
    }
    const def = context.properties[propertyId];
    if (def?.type !== "relation") {
      throw new Error(`Property '${relationName}' is not a relation`);
    }
    const raw = (note.databaseProperties ?? {})[propertyId];
    // `def` comes from THIS row's schema, so its linkedDatabaseId is correct
    // even when the row belongs to a related datasource (nested aggregation).
    return {
      propertyId,
      ids: relationCellIds(raw),
      linkedDatabaseId: def.linkedDatabaseId,
    };
  };

  /** Resolve related rows via the injected resolver (never fetches). */
  const relatedRows = (relationName: string): RelatedRowContext[] => {
    const { propertyId, ids, linkedDatabaseId } = relationCell(relationName);
    if (ids.length === 0) return [];
    if (!context.resolveRelation) {
      throw new Error(
        `Relation formulas need the board context (no relation resolver is wired for '${relationName}')`
      );
    }
    const resolved = context.resolveRelation(propertyId, ids, linkedDatabaseId);
    if (resolved === null) {
      throw new Error(
        `Related rows for '${relationName}' are not loaded yet`
      );
    }
    return resolved;
  };

  /** Runtime context for a related datasource (cached per schema object). */
  const relatedRuntime = (
    properties: Record<string, FormulaPropertyDefinition>
  ): FormulaRuntimeContext => {
    const cached = relatedRuntimeCache.get(properties);
    if (cached) {
      // Depth must reflect THIS evaluation chain, not the chain that first
      // populated the cache.
      return { ...cached, depth: context.depth + 1 };
    }
    const nameToId = new Map<string, string>();
    Object.entries(properties).forEach(([id, def]) => {
      nameToId.set(def.name, id);
      nameToId.set(def.name.toLowerCase(), id);
    });
    const child: FormulaRuntimeContext = {
      properties,
      nameToId,
      compiledCache: new Map(),
      formulaPropertyIds: Object.entries(properties)
        .filter(([, def]) => def.type === "formula")
        .map(([id]) => id),
      resolveRelation: context.resolveRelation,
      depth: context.depth + 1,
    };
    relatedRuntimeCache.set(properties, child);
    return { ...child, depth: context.depth + 1 };
  };

  /** Evaluate a string sub-expression once per related row. */
  const perRelated = (relationName: string, expression: string): any[] => {
    if (typeof expression !== "string" || !expression.trim()) {
      throw new Error("aggregate functions require a string sub-expression");
    }
    if (context.depth >= MAX_RELATION_DEPTH) {
      throw new Error(
        `Relation formula depth limit (${MAX_RELATION_DEPTH}) exceeded — check for mutually recursive formulas`
      );
    }
    const rows = relatedRows(relationName);
    return rows.map((row) => {
      const childContext = relatedRuntime(row.properties);
      const compiled = compileFormula(expression, childContext);
      const childCtx = buildExecutionContext(
        "__sub__",
        row.note,
        childContext,
        new Map(),
        new Set()
      );
      return compiled.fn(childCtx);
    });
  };

  // Short-circuit functions receive their arguments as thunks (see
  // sanitizeExpression) so unused branches are never evaluated. resolve() unwraps
  // a thunk on demand while still tolerating a plain value for safety.
  const resolve = (value: any): any => (typeof value === "function" ? value() : value);

  const ctx: FormulaExecutionContext = {
    prop: propFn,
    // Local midnight, matching how toDate() now reads a "YYYY-MM-DD" cell, so
    // both sides of dateBetween(today(), prop("date")) share one anchor.
    today: () => toDayOnly(new Date()),
    relatedCount: (relationName: string) => relatedRows(relationName).length,
    countIf: (relationName: string, expression: string) =>
      perRelated(relationName, expression).filter(Boolean).length,
    sumBy: (relationName: string, expression: string) =>
      perRelated(relationName, expression)
        .map(Number)
        .filter((n) => Number.isFinite(n))
        .reduce((acc, n) => acc + n, 0),
    someBy: (relationName: string, expression: string) =>
      perRelated(relationName, expression).some(Boolean),
    everyBy: (relationName: string, expression: string) =>
      perRelated(relationName, expression).every(Boolean),
    joinBy: (relationName: string, expression: string, separator = ", ") =>
      perRelated(relationName, expression)
        .filter((v) => v !== null && v !== undefined && v !== "" && v !== false)
        .map((v) => (v instanceof Date ? v.toISOString() : String(v)))
        .join(typeof separator === "string" ? separator : ", "),
    if: (condition, truthy, falsy) =>
      resolve(condition) ? resolve(truthy) : resolve(falsy),
    // ifs(cond1, val1, cond2, val2, ..., [default]) — returns the value paired
    // with the FIRST truthy condition; a trailing odd argument is the default
    // (returned when no condition matches). Arguments are thunks (see
    // SHORT_CIRCUIT_FUNCTIONS) so only the winning branch is ever evaluated.
    ifs: (...args: any[]) => {
      let i = 0;
      for (; i + 1 < args.length; i += 2) {
        if (resolve(args[i])) return resolve(args[i + 1]);
      }
      // Trailing default (odd argument count); otherwise no match → null.
      return i < args.length ? resolve(args[i]) : null;
    },
    and: (...values: any[]) => values.every((value) => Boolean(resolve(value))),
    or: (...values: any[]) => values.some((value) => Boolean(resolve(value))),
    not: (value: any) => !value,
    concat: (...values: any[]) =>
      values
        .map((value) =>
          value instanceof Date ? value.toISOString() : value ?? ""
        )
        .join(""),
    format: (value: any, locale?: string) => {
      if (value == null) return "";
      if (value instanceof Date) return value.toISOString();
      if (typeof value === "number") {
        return Number.isFinite(value)
          ? value.toLocaleString(locale)
          : "";
      }
      return String(value);
    },
    length: (value: any) => {
      if (value == null) return 0;
      if (typeof value === "string") return value.length;
      if (Array.isArray(value)) return value.length;
      if (value instanceof Date) return value.toISOString().length;
      if (typeof value === "object") return Object.keys(value).length;
      return String(value).length;
    },
    abs: (value: any) => {
      const num = Number(value);
      if (!Number.isFinite(num)) {
        throw new Error("abs() requires a numeric value");
      }
      return Math.abs(num);
    },
    round: (value: any, precision = 0) => roundWithPrecision(value, precision),
    floor: (value: any) => {
      const num = Number(value);
      if (!Number.isFinite(num)) {
        throw new Error("floor() requires a numeric value");
      }
      return Math.floor(num);
    },
    ceil: (value: any) => {
      const num = Number(value);
      if (!Number.isFinite(num)) {
        throw new Error("ceil() requires a numeric value");
      }
      return Math.ceil(num);
    },
    max: (...values: any[]) => {
      const nums = values.map(Number).filter((num) => Number.isFinite(num));
      return nums.length === 0 ? null : Math.max(...nums);
    },
    min: (...values: any[]) => {
      const nums = values.map(Number).filter((num) => Number.isFinite(num));
      return nums.length === 0 ? null : Math.min(...nums);
    },
    sum: (...values: any[]) => {
      const nums = values.map(Number).filter((num) => Number.isFinite(num));
      return nums.length === 0 ? null : nums.reduce((acc, val) => acc + val, 0);
    },
    average: (...values: any[]) => {
      const nums = values.map(Number).filter((num) => Number.isFinite(num));
      if (nums.length === 0) return null;
      return nums.reduce((acc, val) => acc + val, 0) / nums.length;
    },
    contains: (target: any, search: any) => {
      if (target == null || search == null) return false;
      if (Array.isArray(target)) {
        return target.some((item) => String(item) === String(search));
      }
      return String(target).includes(String(search));
    },
    empty: (value: any) => {
      if (value == null) return true;
      if (typeof value === "string") return value.trim() === "";
      if (Array.isArray(value)) return value.length === 0;
      if (value instanceof Date) return Number.isNaN(value.getTime());
      if (typeof value === "object") return Object.keys(value).length === 0;
      return false;
    },
    toNumber: (value: any) => {
      if (value == null) return null;
      if (typeof value === "number" && Number.isFinite(value)) return value;
      const converted = Number(value);
      return Number.isFinite(converted) ? converted : null;
    },
    toString: (value: any) => {
      if (value == null) return "";
      if (value instanceof Date) return value.toISOString();
      if (typeof value === "object") return JSON.stringify(value);
      return String(value);
    },
    coalesce: (...values: any[]) => {
      // Arguments arrive as thunks (see SHORT_CIRCUIT_FUNCTIONS): resolve one
      // at a time and stop at the first non-empty, so a later fallback that
      // would error is never evaluated once an earlier value wins.
      for (const value of values) {
        const resolved = resolve(value);
        if (resolved !== null && resolved !== undefined && resolved !== "") {
          return resolved;
        }
      }
      return null;
    },
    now: () => new Date(),
    dateBetween: (date1: any, date2: any, unit: DateDiffUnit = "days") =>
      dateDiff(date1, date2, unit),
    dateAdd: (value: any, amount: number, unit: DateDiffUnit = "days") =>
      dateAdd(value, amount, unit),
    dateSubtract: (value: any, amount: number, unit: DateDiffUnit = "days") =>
      dateAdd(value, -amount, unit),
    timestamp: (value?: any) => {
      if (value === undefined) {
        return Date.now();
      }
      const date = toDate(value);
      return date ? date.getTime() : null;
    },
    pi: () => Math.PI,
    e: () => Math.E,
    cbrt: (value: any) => Math.cbrt(toNumberOrThrow(value, "cbrt", 1)),
    exp: (value: any) => Math.exp(toNumberOrThrow(value, "exp", 1)),
    ln: (value: any) => {
      const num = toNumberOrThrow(value, "ln", 1);
      if (num <= 0) {
        throw new Error("ln() requires a positive number");
      }
      return Math.log(num);
    },
    log10: (value: any) => {
      const num = toNumberOrThrow(value, "log10", 1);
      if (num <= 0) {
        throw new Error("log10() requires a positive number");
      }
      return Math.log10(num);
    },
    log: (value: any, base: any = 10) => {
      const num = toNumberOrThrow(value, "log", 1);
      const baseNum = toNumberOrThrow(base, "log", 2);
      if (num <= 0 || baseNum <= 0 || baseNum === 1) {
        throw new Error("log() requires positive number and base not equal to 1");
      }
      return Math.log(num) / Math.log(baseNum);
    },
    pow: (value: any, exponent: any) => Math.pow(
      toNumberOrThrow(value, "pow", 1),
      toNumberOrThrow(exponent, "pow", 2)
    ),
    sqrt: (value: any) => {
      const num = toNumberOrThrow(value, "sqrt", 1);
      if (num < 0) {
        throw new Error("sqrt() requires a non-negative number");
      }
      return Math.sqrt(num);
    },
    mod: (value: any, divisor: any) => {
      const dividend = toNumberOrThrow(value, "mod", 1);
      const divisorNum = toNumberOrThrow(divisor, "mod", 2);
      if (Math.abs(divisorNum) < EPSILON) {
        throw new Error("mod() divisor cannot be 0");
      }
      return dividend - divisorNum * Math.floor(dividend / divisorNum);
    },
    roundup: (value: any, precision = 0) => {
      const num = toNumberOrThrow(value, "roundup", 1);
      const prec = clampPrecision(toNumberOrThrow(precision, "roundup", 2));
      const factor = 10 ** prec;
      return Math.ceil(num * factor) / factor;
    },
    rounddown: (value: any, precision = 0) => {
      const num = toNumberOrThrow(value, "rounddown", 1);
      const prec = clampPrecision(toNumberOrThrow(precision, "rounddown", 2));
      const factor = 10 ** prec;
      return Math.floor(num * factor) / factor;
    },
    sign: (value: any) => Math.sign(toNumberOrThrow(value, "sign", 1)),
    sin: (value: any) => Math.sin(toNumberOrThrow(value, "sin", 1)),
    cos: (value: any) => Math.cos(toNumberOrThrow(value, "cos", 1)),
    tan: (value: any) => Math.tan(toNumberOrThrow(value, "tan", 1)),
    asin: (value: any) => Math.asin(toNumberOrThrow(value, "asin", 1)),
    acos: (value: any) => Math.acos(toNumberOrThrow(value, "acos", 1)),
    atan: (value: any) => Math.atan(toNumberOrThrow(value, "atan", 1)),
    equal: (left: any, right: any) => compareValues(left, right) === 0,
    unequal: (left: any, right: any) => compareValues(left, right) !== 0,
    larger: (left: any, right: any) => compareValues(left, right) > 0,
    largerEq: (left: any, right: any) => compareValues(left, right) >= 0,
    smaller: (left: any, right: any) => compareValues(left, right) < 0,
    smallerEq: (left: any, right: any) => compareValues(left, right) <= 0,
    lower: (value: any) => toStringValue(value).toLowerCase(),
    upper: (value: any) => toStringValue(value).toUpperCase(),
    trim: (value: any) => toStringValue(value).trim(),
    replace: (text: any, pattern: any, replacement: any) => {
      const target = toStringValue(text);
      const matcher = parsePattern(pattern);
      return typeof matcher === "string"
        ? target.replace(matcher, toStringValue(replacement))
        : target.replace(matcher, toStringValue(replacement));
    },
    replaceAll: (text: any, pattern: any, replacement: any) => {
      const target = toStringValue(text);
      const matcher = parsePattern(pattern);
      if (typeof matcher === "string") {
        return target.split(matcher).join(toStringValue(replacement));
      }
      const globalMatcher = matcher.global ? matcher : new RegExp(matcher.source, `${matcher.flags}g`);
      return target.replace(globalMatcher, toStringValue(replacement));
    },
    slice: (text: any, start: any, length?: any) => {
      const str = toStringValue(text);
      const startIndex = Math.trunc(toNumberOrThrow(start, "slice", 2));
      if (length === undefined || length === null) {
        return str.slice(startIndex);
      }
      const sliceLength = Math.trunc(toNumberOrThrow(length, "slice", 3));
      const endIndex = startIndex + Math.max(0, sliceLength);
      return str.slice(startIndex, endIndex);
    },
    startsWith: (text: any, search: any) => toStringValue(text).startsWith(toStringValue(search)),
    endsWith: (text: any, search: any) => toStringValue(text).endsWith(toStringValue(search)),
    join: (value: any, separator: any = "") => {
      const list = Array.isArray(value) ? value : value == null ? [] : [value];
      return list.map((item) => toStringValue(item)).join(toStringValue(separator));
    },
    // Date-part readers use UTC getters for date-only values (today(),
    // "YYYY-MM-DD" cells are anchored at UTC midnight) so the calendar date
    // survives in every timezone; datetimes read in local time.
    // Every value is now local-anchored, so these read local parts throughout —
    // no UTC branch, and no disagreement with formatDate on the same input.
    year: (date: any) => {
      const d = toDate(date);
      return d ? d.getFullYear() : null;
    },
    month: (date: any) => {
      const d = toDate(date);
      return d ? d.getMonth() + 1 : null;
    },
    date: (date: any) => {
      const d = toDate(date);
      return d ? d.getDate() : null;
    },
    day: (date: any) => {
      const d = toDate(date);
      return d ? d.getDay() : null;
    },
    // A day-only value has no clock. These used to report the local rendering
    // of UTC midnight — 5 and 30 in a +05:30 zone — while formatDate said 00:00.
    hour: (date: any) => {
      const d = toDate(date);
      return d ? (isDateOnlyValue(d) ? 0 : d.getHours()) : null;
    },
    minute: (date: any) => {
      const d = toDate(date);
      return d ? (isDateOnlyValue(d) ? 0 : d.getMinutes()) : null;
    },
    second: (date: any) => {
      const d = toDate(date);
      return d ? (isDateOnlyValue(d) ? 0 : d.getSeconds()) : null;
    },
    formatDate: (date: any, formatStr: string) => {
      const d = toDate(date);
      if (!d) return "";
      // All values are local-anchored now; only the clock differs — a day-only
      // value has none.
      const isDateOnly = isDateOnlyValue(d);
      const year = d.getFullYear();
      const monthIdx = d.getMonth();
      const dayOfMonth = d.getDate();
      const weekdayIdx = d.getDay();
      const hours = isDateOnly ? 0 : d.getHours();
      const minutes = isDateOnly ? 0 : d.getMinutes();
      const seconds = isDateOnly ? 0 : d.getSeconds();
      const pad = (n: number) => String(n).padStart(2, "0");
      // ISO-8601 week number (Monday-start, week containing the first
      // Thursday of the year is week 1) — Notion templates compare
      // formatDate(date, "YYYY-WW") to bucket rows by week.
      const isoWeek = (() => {
        const t = new Date(Date.UTC(year, monthIdx, dayOfMonth));
        const dayNum = (t.getUTCDay() + 6) % 7;
        t.setUTCDate(t.getUTCDate() - dayNum + 3);
        const firstThursday = new Date(Date.UTC(t.getUTCFullYear(), 0, 4));
        const firstDayNum = (firstThursday.getUTCDay() + 6) % 7;
        firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNum + 3);
        return (
          1 +
          Math.round((t.getTime() - firstThursday.getTime()) / (7 * 86400000))
        );
      })();
      const tokens: Record<string, string> = {
        YYYY: String(year),
        YY: pad(year % 100),
        Y: String(year),
        WW: pad(isoWeek),
        W: String(isoWeek),
        MMMM: MONTH_NAMES[monthIdx] ?? "",
        MMM: (MONTH_NAMES[monthIdx] ?? "").slice(0, 3),
        MM: pad(monthIdx + 1),
        M: String(monthIdx + 1),
        DD: pad(dayOfMonth),
        D: String(dayOfMonth),
        dddd: WEEKDAY_NAMES[weekdayIdx] ?? "",
        ddd: (WEEKDAY_NAMES[weekdayIdx] ?? "").slice(0, 3),
        HH: pad(hours),
        H: String(hours),
        hh: pad(hours % 12 === 0 ? 12 : hours % 12),
        h: String(hours % 12 === 0 ? 12 : hours % 12),
        mm: pad(minutes),
        m: String(minutes),
        ss: pad(seconds),
        s: String(seconds),
        A: hours < 12 ? "AM" : "PM",
        a: hours < 12 ? "am" : "pm",
      };
      // Single pass, longest token first — a replace chain would corrupt
      // composite tokens (e.g. "MMMM" becoming two padded month numbers).
      return String(formatStr || "").replace(
        /YYYY|YY|Y|MMMM|MMM|MM|M|WW|W|DD|D|dddd|ddd|HH|H|hh|h|mm|m|ss|s|A|a/g,
        (t) => tokens[t] ?? t
      );
    },
    style: (text: any, ...styles: string[]) => {
      const content = toStringValue(text);
      if (styles.length === 0) return content;
      return JSON.stringify({
        __styled__: true,
        text: content,
        styles: styles.map(s => String(s).toLowerCase()),
      });
    },
  };

  return ctx;
}

/** The row stamps a note-like carries (use-computedNotes copies them off the block). */
function rowStamps(note: FormulaNoteLike): RowMeta {
  return {
    createdAt: note.createdAt,
    createdBy: note.createdBy,
    updatedAt: note.updatedAt,
    lastEditedBy: note.lastEditedBy,
  };
}

function readPropertyValue(
  propertyId: string,
  property: FormulaPropertyDefinition,
  note: FormulaNoteLike,
  context?: FormulaRuntimeContext
) {
  const data = note.databaseProperties ?? {};
  if (property.type === "title") {
    return note.title ?? "";
  }
  if (property.type === "id") {
    return data[propertyId];
  }
  // System time columns come from the row's own stamps.
  if (isSystemTimeType(property.type)) {
    return readSystemPropertyValue(rowStamps(note), property.type);
  }
  // Person cells store member objects; formulas want the display name
  // (`prop("Assignee") == "Alex"`). Same fields rollups read. Entries without
  // a name field fall through unchanged.
  if (property.type === "person" || isSystemPersonType(property.type)) {
    const personName = (v: unknown): unknown => {
      if (!v || typeof v !== "object") return v;
      const p = v as Record<string, unknown>;
      return p.userName ?? p.userEmail ?? p.name ?? v;
    };
    const raw = isSystemPersonType(property.type)
      ? readSystemPropertyValue(rowStamps(note), property.type)
      : data[propertyId];
    return Array.isArray(raw) ? raw.map(personName) : personName(raw);
  }
  // Relation cells store related-row ids; formulas want the row titles, as a
  // list (so length/contains/join behave like they do for multi-select).
  // prop() is a passive read, so when the board context is not wired or the
  // related datasource is not hydrated we fall back to the raw ids instead of
  // throwing the way the explicit aggregate functions do.
  if (property.type === "relation") {
    const ids = relationCellIds(data[propertyId]);
    if (ids.length === 0) return [];
    const rows = context?.resolveRelation?.(
      propertyId,
      ids,
      property.linkedDatabaseId
    );
    if (!rows) return ids;
    return rows.map((row) => row.note.title ?? "");
  }
  // Select-family cells store the option ID; formulas compare against the
  // display NAME (`prop("status") == "Done"`). Resolve id → name here, the
  // same translation rollups already perform — without it every select
  // comparison silently mismatched. Unknown ids fall through unchanged.
  if (
    property.type === "select" ||
    property.type === "status" ||
    property.type === "priority" ||
    property.type === "multi_select"
  ) {
    const optionName = (v: unknown): unknown => {
      if (typeof v !== "string") return v;
      const opt = (property.options ?? []).find(
        (o: any) => String((o as any)?.id ?? "") === v
      );
      return (opt as any)?.name ?? v;
    };
    const raw = data[propertyId];
    return Array.isArray(raw) ? raw.map(optionName) : optionName(raw);
  }
  return data[propertyId];
}

function coerceValueForReturnType(
  rawValue: any,
  expectedType: FormulaReturnType
): { value: any; error?: string } {
  if (rawValue === undefined || rawValue === null) {
    return { value: null };
  }

  switch (expectedType) {
    case "text": {
      if (typeof rawValue === "string") {
        return { value: rawValue };
      }
      if (rawValue instanceof Date) {
        return { value: rawValue.toISOString() };
      }
      if (typeof rawValue === "object") {
        try {
          return { value: JSON.stringify(rawValue) };
        } catch {
          return { value: String(rawValue) };
        }
      }
      return { value: String(rawValue) };
    }
    case "number": {
      if (typeof rawValue === "number" && Number.isFinite(rawValue)) {
        return { value: rawValue };
      }
      const converted = Number(rawValue);
      if (Number.isFinite(converted)) {
        return { value: converted };
      }
      return { value: null, error: "Formula result could not be converted to number" };
    }
    case "boolean": {
      if (typeof rawValue === "boolean") {
        return { value: rawValue };
      }
      if (typeof rawValue === "string") {
        const lower = rawValue.toLowerCase();
        if (lower === "true") return { value: true };
        if (lower === "false") return { value: false };
      }
      return { value: Boolean(rawValue) };
    }
    case "date": {
      const date = toDate(rawValue);
      if (!date) {
        return { value: null, error: "Formula result could not be converted to date" };
      }
      // Naive local datetime, like date cells: filters read the day off the
      // string. includeTime follows the value — it was hardcoded true, so a
      // day-only result was stored with a phantom clock (and, west of UTC,
      // under the previous day, since today() was anchored at UTC midnight).
      return { value: serializeDateCell(date, null, !isDateOnlyValue(date)) };
    }
    default:
      return { value: rawValue };
  }
}

function roundWithPrecision(value: any, precision = 0): number {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    throw new Error("round() requires a numeric value");
  }

  const factor = 10 ** clampPrecision(precision);
  return Math.round(num * factor) / factor;
}

function clampPrecision(precision: number): number {
  if (!Number.isFinite(precision)) return 0;
  if (precision > 10) return 10;
  if (precision < -10) return -10;
  return Math.trunc(precision);
}

function dateDiff(date1: any, date2: any, unit: DateDiffUnit): number | null {
  const d1 = toDate(date1);
  const d2 = toDate(date2);
  if (!d1 || !d2) return null;

  const diffMs = d1.getTime() - d2.getTime();
  // Between two day-only values, count calendar days rather than elapsed time:
  // local midnight to local midnight is 23 or 25 hours across a DST boundary,
  // which Math.trunc would report as 0 days.
  const calendarDays = () =>
    (Date.UTC(d1.getFullYear(), d1.getMonth(), d1.getDate()) -
      Date.UTC(d2.getFullYear(), d2.getMonth(), d2.getDate())) /
    86400000;
  const bothDayOnly = isDateOnlyValue(d1) && isDateOnlyValue(d2);

  switch (unit) {
    case "milliseconds":
      return diffMs;
    case "seconds":
      return Math.trunc(diffMs / 1000);
    case "minutes":
      return Math.trunc(diffMs / (1000 * 60));
    case "hours":
      return Math.trunc(diffMs / (1000 * 60 * 60));
    case "days":
      return bothDayOnly ? calendarDays() : Math.trunc(diffMs / (1000 * 60 * 60 * 24));
    case "weeks":
      return bothDayOnly
        ? Math.trunc(calendarDays() / 7)
        : Math.trunc(diffMs / (1000 * 60 * 60 * 24 * 7));
    case "months": {
      const years = d1.getFullYear() - d2.getFullYear();
      const months = d1.getMonth() - d2.getMonth();
      let totalMonths = years * 12 + months;

      // Adjust if d1's day is less than d2's day (full month check)
      if (d1.getDate() < d2.getDate()) {
        totalMonths--;
      }
      return totalMonths;
    }
    case "quarters": {
      const years = d1.getFullYear() - d2.getFullYear();
      const months = d1.getMonth() - d2.getMonth();
      let totalMonths = years * 12 + months;
      if (d1.getDate() < d2.getDate()) {
        totalMonths--;
      }
      return Math.trunc(totalMonths / 3);
    }
    case "years": {
      let years = d1.getFullYear() - d2.getFullYear();
      const mMatch = d1.getMonth() - d2.getMonth();
      if (mMatch < 0 || (mMatch === 0 && d1.getDate() < d2.getDate())) {
        years--;
      }
      return years;
    }
    default:
      return Math.trunc(diffMs / (1000 * 60 * 60 * 24));
  }
}

function dateAdd(value: any, amount: number, unit: DateDiffUnit): Date | null {
  const date = toDate(value);
  if (!date || !Number.isFinite(amount)) return null;

  const result = new Date(date);
  switch (unit) {
    case "milliseconds":
      result.setMilliseconds(result.getMilliseconds() + amount);
      break;
    case "seconds":
      result.setSeconds(result.getSeconds() + amount);
      break;
    case "minutes":
      result.setMinutes(result.getMinutes() + amount);
      break;
    case "hours":
      result.setHours(result.getHours() + amount);
      break;
    case "days":
      result.setDate(result.getDate() + amount);
      break;
    case "weeks":
      result.setDate(result.getDate() + amount * 7);
      break;
    case "months":
      result.setMonth(result.getMonth() + amount);
      break;
    // "quarters" is a declared DateDiffUnit and dateBetween implements it, but
    // this switch had no case — so it fell through to default and added DAYS.
    case "quarters":
      result.setMonth(result.getMonth() + amount * 3);
      break;
    case "years":
      result.setFullYear(result.getFullYear() + amount);
      break;
    default:
      result.setDate(result.getDate() + amount);
      break;
  }
  // A day-only value stays day-only under day-scale arithmetic. Re-anchoring
  // matters across a DST boundary, where the local setters move the instant 23
  // or 25 hours and would otherwise leave it at 23:00 of the day before.
  if (isDateOnlyValue(date) && DAY_SCALE_UNITS.has(unit)) return toDayOnly(result);
  return result;
}

function toDate(value: any): Date | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    // Read stored cells through the same parser the board uses. `new Date()`
    // applied three contracts at once — UTC midnight for "2026-03-10", local
    // for "2026-03-10T09:30", and NaN for comma ranges and the legacy bracket
    // form, so every formula over a range row silently evaluated to nothing.
    const cell = parseDateCell(trimmed);
    if (cell) return cell.hasTime ? cell.start : markDayOnly(cell.start);
    const date = new Date(trimmed);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
}

export function buildFormulaDefinitionsFromSchema(
  properties: Record<string, any>
): Record<string, FormulaPropertyDefinition> {
  return Object.entries(properties).reduce((acc, [id, schema]) => {
    acc[id] = {
      id,
      name: schema.name,
      type: schema.type,
      formula: schema.type === "formula" ? schema.formula ?? "" : undefined,
      formulaReturnType: schema.formulaReturnType,
      options: schema.options,
      linkedDatabaseId:
        schema.type === "relation" ? schema.linkedDatabaseId : undefined,
    };
    return acc;
  }, {} as Record<string, FormulaPropertyDefinition>);
}

/**
 * Rewrite typed property NAMES to their ids in a formula string, so a SAVED
 * formula survives a later rename. Run this at save time against the schema of
 * the datasource the formula belongs to.
 *
 * Only the direct argument of `prop(...)` and the relation-NAME argument of the
 * relation functions are rewritten. Left untouched: an argument that already is
 * an id, a name that doesn't resolve in this schema, a name that is AMBIGUOUS
 * (shared by more than one property — there is no way to know which was meant,
 * so we never guess; inserting the property stores its exact id instead), and
 * everything inside sub-expression string literals (those reference a related
 * table's schema, which this schema can't resolve). Original formatting is
 * preserved (position-based splice); an unparseable formula is returned
 * unchanged.
 */
type NormalizeSchema = Record<
  string,
  { name?: string; linkedDatabaseId?: string } | undefined
>;

export function normalizeFormulaReferencesToIds(
  formula: string,
  properties: NormalizeSchema,
  /**
   * Resolves a relation's linked datasource id to THAT datasource's schema, so
   * references inside a relation sub-expression (e.g. prop('Status') in
   * countIf("Tasks", "prop('Status') == 'Done'")) are normalized against the
   * LINKED table, not the current one. Omit to leave sub-expressions untouched.
   */
  resolveLinkedSchema?: (linkedDatabaseId: string) => NormalizeSchema | undefined,
  /**
   * Quote character to emit around ids. Double at the top level (so refs render
   * as chips); the recursive sub-expression pass uses SINGLE quotes so the ids
   * sit inside the double-quoted sub-expression string WITHOUT needing backslash
   * escapes (`"prop('id')"` instead of `"prop(\"id\")"`).
   */
  innerQuote: '"' | "'" = '"'
): string {
  if (typeof formula !== "string" || !formula.trim()) return formula;

  // Clean up nested quotes from inserting a property inside typed quotes
  // (`"prop("id")"` → `prop("id")`) before doing anything else.
  formula = repairInsertedPropQuotes(formula);

  let tokens: Token[];
  try {
    tokens = tokenize(formula);
  } catch {
    return formula; // never corrupt something we can't parse
  }

  // Map each name (and its lowercased form) to the SET of ids that use it, so a
  // name shared by multiple properties is detected as ambiguous and skipped.
  const exactIds = new Map<string, Set<string>>();
  const lowerIds = new Map<string, Set<string>>();
  const add = (m: Map<string, Set<string>>, key: string, id: string) => {
    const set = m.get(key) ?? new Set<string>();
    set.add(id);
    m.set(key, set);
  };
  for (const [id, def] of Object.entries(properties || {})) {
    const name = def?.name;
    if (typeof name === "string" && name) {
      add(exactIds, name, id);
      add(lowerIds, name.toLowerCase(), id);
    }
  }
  const only = (set?: Set<string>): string | undefined =>
    set && set.size === 1 ? [...set][0] : undefined; // size > 1 → ambiguous

  const resolveId = (raw: string): string | undefined => {
    if (properties[raw]) return undefined; // already an id — leave it
    return only(exactIds.get(raw)) ?? only(lowerIds.get(raw.toLowerCase()));
  };
  // The actual id for a value that may be a name OR already an id.
  const toId = (raw: string): string | undefined =>
    properties[raw] ? raw : resolveId(raw);

  const edits: Array<{ start: number; end: number; text: string }> = [];
  for (let i = 0; i < tokens.length; i++) {
    const fn = tokens[i]!;
    if (fn.type !== TokenType.Identifier) continue;
    if (fn.value !== "prop" && !RELATION_NAME_FUNCTIONS.has(fn.value)) continue;
    const open = tokens[i + 1];
    const strTok = tokens[i + 2];
    if (open?.type !== TokenType.Paren || open.value !== "(") continue;
    if (strTok?.type !== TokenType.String) continue;
    let name: string;
    try {
      name = parseStringLiteral(strTok.value, strTok.position);
    } catch {
      continue;
    }
    const id = resolveId(name);
    if (!id) continue;
    // prop("name") → prop("id"). A BARE relation-function name → prop("id") too
    // (wrapped), so it renders as a chip like every other reference; the engine
    // unwraps prop() in a relation argument back to the id at compile time.
    const q = innerQuote;
    const text =
      fn.value === "prop"
        ? `${q}${id}${q}`
        : `prop(${q}${id}${q})`;
    edits.push({
      start: strTok.position,
      end: strTok.position + strTok.value.length,
      text,
    });
  }

  // Second pass: normalize each relation function's SUB-EXPRESSION against the
  // LINKED datasource's schema (its refs point at the related table, not this
  // one). Only runs when a linked-schema resolver is supplied.
  if (resolveLinkedSchema) {
    // Token ranges [start, end) of the top-level arguments of the call whose
    // "(" token is at openIdx.
    const callArguments = (openIdx: number): Array<[number, number]> => {
      const args: Array<[number, number]> = [];
      let depth = 0;
      let argStart = openIdx + 1;
      for (let j = openIdx; j < tokens.length; j++) {
        const t = tokens[j]!;
        if (t.type === TokenType.Paren && t.value === "(") depth++;
        else if (t.type === TokenType.Paren && t.value === ")") {
          depth--;
          if (depth === 0) {
            args.push([argStart, j]);
            break;
          }
        } else if (t.type === TokenType.Comma && depth === 1) {
          args.push([argStart, j]);
          argStart = j + 1;
        }
      }
      return args;
    };
    // Relation property id from arg[0]: bare "Name" or inserted prop("id").
    const relationIdFromArg = ([start, end]: [number, number]): string | undefined => {
      try {
        if (end - start === 1 && tokens[start]?.type === TokenType.String) {
          return toId(parseStringLiteral(tokens[start]!.value, tokens[start]!.position));
        }
        if (
          tokens[start]?.type === TokenType.Identifier &&
          tokens[start]!.value === "prop" &&
          tokens[start + 2]?.type === TokenType.String
        ) {
          return toId(
            parseStringLiteral(tokens[start + 2]!.value, tokens[start + 2]!.position)
          );
        }
      } catch {
        return undefined;
      }
      return undefined;
    };

    for (let i = 0; i < tokens.length; i++) {
      const fn = tokens[i]!;
      if (fn.type !== TokenType.Identifier || !RELATION_NAME_FUNCTIONS.has(fn.value)) continue;
      if (tokens[i + 1]?.type !== TokenType.Paren || tokens[i + 1]!.value !== "(") continue;
      const args = callArguments(i + 1);
      if (args.length < 2) continue;
      const relId = relationIdFromArg(args[0]!);
      const linkedDbId = relId ? properties[relId]?.linkedDatabaseId : undefined;
      if (!linkedDbId) continue;
      const [sStart, sEnd] = args[1]!;
      if (sEnd - sStart !== 1 || tokens[sStart]?.type !== TokenType.String) continue;
      const linkedSchema = resolveLinkedSchema(linkedDbId);
      if (!linkedSchema) continue;
      const subTok = tokens[sStart]!;
      let inner: string;
      try {
        inner = parseStringLiteral(subTok.value, subTok.position);
      } catch {
        continue;
      }
      const normalizedInner = normalizeFormulaReferencesToIds(
        inner,
        linkedSchema,
        resolveLinkedSchema,
        "'" // single-quote ids inside the sub-expression → no backslash escapes
      );
      if (normalizedInner !== inner) {
        edits.push({
          start: subTok.position,
          end: subTok.position + subTok.value.length,
          text: JSON.stringify(normalizedInner),
        });
      }
    }
  }

  if (edits.length === 0) return formula;
  // Apply right-to-left so earlier splices don't shift later positions.
  edits.sort((a, b) => b.start - a.start);
  let out = formula;
  for (const e of edits) {
    out = out.slice(0, e.start) + e.text + out.slice(e.end);
  }
  return out;
}

function isWhitespace(char: string): boolean {
  return /\s/.test(char);
}

function isDigit(char: string): boolean {
  return /[0-9]/.test(char);
}

function isIdentifierStart(char: string): boolean {
  return /[A-Za-z_]/.test(char);
}

function isIdentifierPart(char: string): boolean {
  return /[A-Za-z0-9_]/.test(char);
}
